import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Sparkles, Circle, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import wsClient from '../utils/websocket';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Housekeeping = () => {
  const { selectedProperty } = useOutletContext();
  const [units, setUnits] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [wsConnected, setWsConnected] = useState(false);

  useEffect(() => {
    if (selectedProperty) {
      fetchData();
    }
  }, [selectedProperty]);

  // WebSocket integration for real-time room status updates
  useEffect(() => {
    if (!selectedProperty) return;

    const token = localStorage.getItem('access_token');
    if (!token) return;

    // Connect WebSocket
    wsClient.connect(selectedProperty.id, token);

    // Handle connection
    const handleConnection = (data) => {
      setWsConnected(data.connected);
    };

    // Handle room status updates
    const handleRoomStatus = (event) => {
      console.log('[Housekeeping] Room status event:', event.payload);
      // Update room status in real-time
      setUnits(prevUnits => 
        prevUnits.map(unit => 
          unit.id === event.payload.room_id 
            ? { ...unit, status: event.payload.status }
            : unit
        )
      );
    };

    // Handle guest events
    const handleGuestCheckedIn = () => fetchData();
    const handleGuestCheckedOut = () => fetchData();

    // Register handlers
    wsClient.on('connection', handleConnection);
    wsClient.on('room_status.updated', handleRoomStatus);
    wsClient.on('guest.checked_in', handleGuestCheckedIn);
    wsClient.on('guest.checked_out', handleGuestCheckedOut);

    // Polling fallback - 45 seconds
    const pollingInterval = setInterval(() => {
      if (!wsConnected) {
        console.log('[Housekeeping] Polling fallback');
        fetchData();
      }
    }, 45000);

    return () => {
      wsClient.off('connection', handleConnection);
      wsClient.off('room_status.updated', handleRoomStatus);
      wsClient.off('guest.checked_in', handleGuestCheckedIn);
      wsClient.off('guest.checked_out', handleGuestCheckedOut);
      clearInterval(pollingInterval);
    };
  }, [selectedProperty]);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const [unitsRes, tasksRes] = await Promise.all([
        axios.get(`${API}/properties/${selectedProperty.id}/units`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/housekeeping/tasks?property_id=${selectedProperty.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setUnits(unitsRes.data);
      setTasks(tasksRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateUnitStatus = async (unitId, newStatus) => {
    try {
      const token = localStorage.getItem('access_token');
      await axios.patch(
        `${API}/units/${unitId}/status`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Room status updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const updateTaskStatus = async (taskId, newStatus) => {
    try {
      const token = localStorage.getItem('access_token');
      await axios.patch(
        `${API}/housekeeping/tasks/${taskId}?status=${newStatus}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Task updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update task');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      CLEAN: 'bg-emerald-500',
      DIRTY: 'bg-red-500',
      IN_PROGRESS: 'bg-amber-500',
      INSPECTED: 'bg-blue-500',
      OOO: 'bg-slate-500'
    };
    return colors[status] || 'bg-slate-400';
  };

  const getStatusTextColor = (status) => {
    const colors = {
      CLEAN: 'text-emerald-700 bg-emerald-50 border-emerald-200',
      DIRTY: 'text-red-700 bg-red-50 border-red-200',
      IN_PROGRESS: 'text-amber-700 bg-amber-50 border-amber-200',
      INSPECTED: 'text-blue-700 bg-blue-50 border-blue-200',
      OOO: 'text-slate-700 bg-slate-50 border-slate-200'
    };
    return colors[status] || 'text-slate-700 bg-slate-50 border-slate-200';
  };

  const statusOptions = [
    { value: 'CLEAN', label: 'Clean' },
    { value: 'DIRTY', label: 'Dirty' },
    { value: 'IN_PROGRESS', label: 'In Progress' },
    { value: 'INSPECTED', label: 'Inspected' },
    { value: 'OOO', label: 'Out of Order' }
  ];

  if (!selectedProperty) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-slate-600">Please select a property</p>
      </div>
    );
  }

  if (loading) {
    return <div className="flex items-center justify-center h-full">Loading...</div>;
  }

  const cleanRooms = units.filter(u => u.status === 'CLEAN').length;
  const dirtyRooms = units.filter(u => u.status === 'DIRTY').length;
  const inProgressRooms = units.filter(u => u.status === 'IN_PROGRESS').length;

  return (
    <div className="space-y-6" data-testid="housekeeping-page">
      <div>
        <h1 className="text-3xl font-bold text-slate-900">Housekeeping</h1>
        <p className="text-slate-600 mt-1">Room status and cleaning tasks</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="border-l-4 border-l-emerald-500" data-testid="clean-rooms-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-slate-600">Clean Rooms</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-emerald-600">{cleanRooms}</div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-amber-500" data-testid="inprogress-rooms-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-slate-600">In Progress</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-amber-600">{inProgressRooms}</div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-red-500" data-testid="dirty-rooms-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-slate-600">Needs Cleaning</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-red-600">{dirtyRooms}</div>
          </CardContent>
        </Card>
      </div>

      {/* Room Status Grid */}
      <Card data-testid="room-status-grid">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Sparkles size={20} className="text-cyan-600" />
            Room Status Board
          </CardTitle>
        </CardHeader>
        <CardContent>
          {units.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              No rooms configured. Add rooms in Settings.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {units.map((unit) => (
                <div
                  key={unit.id}
                  className="border border-slate-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                  data-testid={`room-card-${unit.id}`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="text-lg font-bold text-slate-900">{unit.name}</div>
                    <div className={`w-3 h-3 rounded-full ${getStatusColor(unit.status)}`}></div>
                  </div>

                  <div className={`text-sm px-3 py-1.5 rounded-full border inline-block mb-3 ${getStatusTextColor(unit.status)}`}>
                    {unit.status.replace('_', ' ')}
                  </div>

                  <div className="flex flex-wrap gap-2">
                    {statusOptions.map((option) => (
                      <Button
                        key={option.value}
                        size="sm"
                        variant={unit.status === option.value ? 'default' : 'outline'}
                        onClick={() => updateUnitStatus(unit.id, option.value)}
                        data-testid={`status-button-${unit.id}-${option.value}`}
                        className="text-xs"
                      >
                        {option.label}
                      </Button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Tasks */}
      <Card data-testid="tasks-list">
        <CardHeader>
          <CardTitle>Today's Tasks</CardTitle>
        </CardHeader>
        <CardContent>
          {tasks.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              No tasks for today
            </div>
          ) : (
            <div className="space-y-3">
              {tasks.map((task) => {
                const unit = units.find(u => u.id === task.unit_id);
                return (
                  <div
                    key={task.id}
                    className="flex items-center justify-between p-4 bg-slate-50 rounded-lg"
                    data-testid={`task-${task.id}`}
                  >
                    <div className="flex items-center gap-3">
                      {task.status === 'COMPLETED' ? (
                        <CheckCircle2 className="text-emerald-500" size={20} />
                      ) : (
                        <Circle className="text-slate-400" size={20} />
                      )}
                      <div>
                        <div className="font-medium text-slate-900">
                          Room {unit?.name || 'Unknown'} - {task.task_type}
                        </div>
                        {task.notes && (
                          <div className="text-sm text-slate-600">{task.notes}</div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant={task.priority === 'URGENT' ? 'destructive' : 'secondary'}>
                        {task.priority}
                      </Badge>
                      {task.status !== 'COMPLETED' && (
                        <Button
                          size="sm"
                          onClick={() => updateTaskStatus(task.id, 'COMPLETED')}
                          data-testid={`complete-task-${task.id}`}
                        >
                          Complete
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Housekeeping;
